/**
 * Bridge message constants. Mirror of
 * `toqio-flow/apps/forms-host/src/bridge/protocol.ts` and lum-web's
 * `src/modules/formsHostView/bridgeMessages.js`.
 *
 * Both sides MUST speak the same protocol. If you change either side,
 * keep the other in sync and bump BRIDGE_VERSION on backwards-incompatible
 * changes.
 */

export const BRIDGE_VERSION = 1;

// Host -> Forms
export const INBOUND_TYPES = {
  INIT: 'init',
  TOKEN_UPDATE: 'token:update',
  THEME_UPDATE: 'theme:update',
  LOCALE_UPDATE: 'locale:update',
  GOTO: 'goto',
  BACK_REQUEST: 'back:request',
} as const;

// Forms -> Host
export const OUTBOUND_TYPES = {
  READY: 'ready',
  FLOW_READY: 'flow:ready',
  RESIZE: 'resize',
  STEP_CHANGE: 'step:change',
  ROUTE_CHANGE: 'route:change',
  SUBMIT_SUCCESS: 'submit:success',
  SUBMIT_ERROR: 'submit:error',
  CANCEL: 'cancel',
  NAVIGATE_NATIVE: 'navigate:native',
  TELEMETRY: 'telemetry',
} as const;

/** Minimal shape we need from a menu/app entry to resolve a custom flow. */
export type MenuAppLike = {
  appSource?: string;
  marketplaceKey?: string;
};

/** Generic flat map of URL params passed through to the flow. */
export type UrlParams = Record<string, string>;

/**
 * The `init.input` payload: a flat passthrough of URL params plus the full
 * `searchParams` map for flows that need it. The index signature widens to
 * `string | UrlParams` so `searchParams` (an object) can coexist with the
 * string-valued params.
 */
export type InitInput = {
  searchParams: UrlParams;
  [key: string]: string | UrlParams;
};

/** The `init` payload sent to the iframe. */
export type InitMessage = {
  type: typeof INBOUND_TYPES.INIT;
  v: number;
  token: string | null;
  tenant?: string;
  locale: string;
  theme: Record<string, unknown>;
  flowId?: string;
  entryStep?: string;
  input: InitInput;
};

/** A parsed inbound message from the iframe (at least a string `type`). */
export type BridgeMessage = {
  type: string;
  [key: string]: unknown;
};
